import moment from 'moment-timezone';
import { CoachingReservation } from 'coaching/containers/GetAvailableReservationsContainer';
import CurrentUser from 'auth/CurrentUser';
import { PurchaserRole } from 'globalTypes';
import {
  clearStorage,
  retrieveFromStorage,
  saveToStorage,
  TutoringStorages,
} from 'lib/TutoringStateMachine/SignUpStorage';
import { Moment } from 'moment-timezone/moment-timezone';
import * as CoachingActions from 'coaching/actions/CoachingActions';

export type Reservation = {
  startDateTime: Moment;
  endDateTime: Moment;
  coachUuid: string;
};

export type PlainReservation = {
  startDateTime: string;
  endDateTime: string;
  coachUuid: string;
};

type PurchaseResult = {
  participantUUID: string;
  linkStudentToken?: string;
};

export type UnauthedPurchaserData = {
  purchaserRole: PurchaserRole;
  email?: string;
  checkPolicies: boolean;
  fullName: string;
};

export type StudentDetails = {
  selectedSubject: string;
  selectedGradeLevel: string;
  selectedChildName?: string;
  selectedStudentUuid?: string;
};

export type StoredTutoringReservation = {
  selectedReservation: Reservation;
  reservationUuid: string;
  selectedUserTimezone: string;
};

/**
 * Functions to get and validate data directly from storage
 */

export function getStudentDetails(): StudentDetails | null {
  const details = retrieveFromStorage(TutoringStorages.studentDetails);
  if (!details) {
    return null;
  }
  const selectedSubject = throwIfEmpty(details.selectedSubject);
  const selectedGradeLevel = throwIfEmpty(details.selectedGradeLevel);

  return {
    selectedSubject,
    selectedGradeLevel,
    selectedChildName: details.selectedChildName,
    selectedStudentUuid: details.selectedStudentUuid,
  };
}

export function getPurchaseCompleted() {
  return !!retrieveFromStorage(TutoringStorages.purchaseCompleted);
}

export function getPurchaseResult(): PurchaseResult | null {
  const data = retrieveFromStorage(TutoringStorages.purchaseResult);
  if (!data) {
    return null;
  }
  throwIfEmpty(data.participantUUID);
  return data;
}

export function getUnauthedPurchaserDetails(): UnauthedPurchaserData | null {
  const data = retrieveFromStorage(TutoringStorages.unauthedPurchaserDetails);
  if (!data) {
    return null;
  }
  const purchaserRole = throwIfNotPurchaserRole(data.purchaserRole);
  const fullName = throwIfEmpty(data.fullName);
  const email = data.email;
  const checkPolicies = throwIfNullish(data.checkPolicies);
  return {
    purchaserRole,
    fullName,
    email,
    checkPolicies,
  };
}

export function getReservationInfo(): StoredTutoringReservation | null {
  let scheduleInfo = retrieveFromStorage(TutoringStorages.savedReservation);
  if (!scheduleInfo) {
    return null;
  }
  const uuid = throwIfNullish(scheduleInfo?.reservationUuid);
  const selectedUserTimezone = throwIfNullish(scheduleInfo?.selectedUserTimezone);
  const plainReservation = throwIfNullish(scheduleInfo.selectedReservation);
  const reservation = fromPlainReservation(plainReservation);
  return {
    selectedReservation: reservation,
    reservationUuid: uuid,
    selectedUserTimezone,
  };
}

export function getAvailabilityQuery(): CoachingActions.CoachingAvailabilityResponse | null {
  return retrieveFromStorage(TutoringStorages.availabilityQuery);
}

export function getStoredReservation(): Reservation | undefined {
  return getReservationInfo()?.selectedReservation;
}

export function getSelectedSubjectOrThrow(): string {
  return throwIfEmpty(getStudentDetails()?.selectedSubject);
}

export function getReservationOrThrow(): CoachingReservation {
  return throwIfNullish(getStoredReservation());
}

export function getUserTimezone(): string {
  const selectedUserTimezone = getReservationInfo()?.selectedUserTimezone;
  return throwIfNullish(selectedUserTimezone);
}

export function getPurchaserRole(): PurchaserRole | undefined {
  // todo: maybe change this so we actually validate the purchaserRole
  const getStartedData = retrieveFromStorage(TutoringStorages.unauthedPurchaserDetails);
  return getStartedData?.purchaserRole;
}

export function getStudentUuid() {
  return getStudentDetails()?.selectedStudentUuid;
}

export function getGradeLevelOrThrow() {
  return throwIfEmpty(getStudentDetails()?.selectedGradeLevel);
}

export function getReservationUuidOrThrow(): string {
  const reservationUuid = getReservationInfo()?.reservationUuid;
  return throwIfNullish(reservationUuid);
}

export function getParticipantUuidOrThrow() {
  return throwIfEmpty(getPurchaseResult()?.participantUUID);
}

export function getLinkStudentCompleted(): boolean {
  return !!retrieveFromStorage(TutoringStorages.linkStudentCompleted);
}

/**
 * Functions that resolve data that might be either in storage, or in the CurrentUser for authed
 * users
 */

export function resolveChildName() {
  return CurrentUser.isSignedIn() ? resolveAuthedStudentName() : resolveUnauthedStudentName();
}

function resolveAuthedStudentName(): string | undefined {
  if (CurrentUser.getUser()?.isStudent()) {
    return CurrentUser.getUser().getFullName();
  }

  return getStudentDetails()?.selectedChildName ?? '';
}

function resolveUnauthedStudentName(): string | undefined {
  const purchaserDetails = getUnauthedPurchaserDetails();
  if (purchaserDetails?.purchaserRole === PurchaserRole.Parent) {
    return getStudentDetails()?.selectedChildName ?? '';
  }

  if (purchaserDetails?.purchaserRole === PurchaserRole.Student) {
    return purchaserDetails?.fullName;
  }
}

export function resolvePurchaserEmail() {
  const getStartedUnauthenticated = retrieveFromStorage(TutoringStorages.unauthedPurchaserDetails);
  const email = CurrentUser.isSignedIn()
    ? CurrentUser.getUser().getEmail()
    : getStartedUnauthenticated?.email;
  return email ?? '';
}

export function resolveFullName() {
  return CurrentUser.isSignedIn()
    ? CurrentUser.getUser().getFullName()
    : getUnauthedPurchaserDetails()?.fullName;
}

/**
 * Functions that save data in storage
 */

export function saveStudentDetails(data: StudentDetails) {
  saveToStorage(TutoringStorages.studentDetails, data);
}

export function saveUnauthedPurchaserDetails(data: UnauthedPurchaserData) {
  saveToStorage(TutoringStorages.unauthedPurchaserDetails, data);
}

export function savePurchaseCompleted(completed: boolean) {
  saveToStorage(TutoringStorages.purchaseCompleted, completed);
}

export function saveLinkStudentCompleted(completed: boolean) {
  saveToStorage(TutoringStorages.linkStudentCompleted, completed);
}

export function savePurchaseResult(result: PurchaseResult) {
  saveToStorage(TutoringStorages.purchaseResult, result);
}

export function saveReservationInfo(reservationInfo: StoredTutoringReservation) {
  const storagePayload = {
    ...reservationInfo,
    selectedReservation: toPlainReservation(reservationInfo.selectedReservation),
  };
  saveToStorage(TutoringStorages.savedReservation, storagePayload);
}

export function saveAvailabilityQuery(data: CoachingActions.CoachingAvailabilityResponse) {
  saveToStorage(TutoringStorages.availabilityQuery, data);
}

export function clearScheduleData() {
  clearStorage(TutoringStorages.savedReservation);
}

export function clearStoredEmail() {
  const storedData = retrieveFromStorage(TutoringStorages.unauthedPurchaserDetails);
  if (storedData === null) {
    return;
  }
  const newData = {
    ...storedData,
    email: undefined,
  };
  saveToStorage(TutoringStorages.unauthedPurchaserDetails, newData);
}

export function throwIfNullish<T>(value: T | undefined | null): T {
  if (value === undefined || value === null) {
    throw new Error('Tutoring validation error: Expected value to be truthy');
  }
  return value;
}

export function throwIfEmpty(value: string | undefined | null): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error('Tutoring validation error: expected a non-empty string');
  }
  return value;
}

export function throwIfNotPurchaserRole(value: string | undefined | null): PurchaserRole {
  if (value !== PurchaserRole.Student && value !== PurchaserRole.Parent) {
    throw new Error('Tutoring validation error: expected a purchaser role');
  }
  return value;
}

export function fromPlainReservation(reservation: PlainReservation): Reservation {
  return {
    startDateTime: moment(reservation.startDateTime),
    endDateTime: moment(reservation.endDateTime),
    coachUuid: reservation.coachUuid,
  };
}

export function toPlainReservation(reservation: CoachingReservation) {
  const { coachUuid, startDateTime, endDateTime } = reservation;
  return {
    coachUuid,
    startDateTime: startDateTime.toISOString(),
    endDateTime: endDateTime.toISOString(),
  };
}
